/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'nex4nova-drilling-language';
const VALID = new Set(['es', 'pt', 'en']);
const FALLBACK = 'es';

const t = {
  es: {
    'app.title': 'Visualización de Perforaciones',
    'nav.map': 'Mapa',
    'nav.stripLogs': 'Perfiles',
    'nav.scene3d': 'Escena 3D',
    'nav.analytics': 'Analítica',
    'project.section': 'Proyecto',
    'project.dataFormat': 'Instrucciones de Formato',
    'project.openFolder': 'Abrir carpeta del proyecto',
    'project.uploadZip': 'Subir proyecto (ZIP)',
    'project.zipInvalid': 'El ZIP no contiene archivos de proyecto válidos.',
    'project.loading': 'Cargando…',
    'project.noProject': 'Ningún proyecto cargado',
    'project.close': 'Cerrar',
    'project.error': '{msg}',
    'demo.section': 'Conjuntos de Demo',
    'demo.download': 'Descargar',
    'options.autoLoad': 'Auto-cargar último proyecto',
    'analytics.title': 'Datos Analíticos',
    'analytics.loadProject': 'Cargar proyecto completo',
    'analytics.selectProject': 'Seleccionar proyecto…',
    'analytics.filterProject': 'Filtrar sondajes por proyecto',
    'analytics.selectProjectFirst': 'Seleccione un proyecto primero…',
    'analytics.loadHole': 'Cargar sondaje individual',
    'analytics.selectHole': 'Seleccionar sondaje…',
    'analytics.chooseProject': 'Elija un proyecto arriba primero…',
    'analytics.addHole': 'Agregar sondaje',
    'analytics.clearData': 'Limpiar datos de sondaje',
    'analytics.source': 'Fuente de datos',
    'analytics.rows': '{n} filas',
    'analytics.drillholeAssays': 'Ensayes de sondajes',
    'analytics.surfaceSamples': 'Muestras de superficie',
    'analytics.scatter': 'Dispersión',
    'analytics.scatterDesc': 'Analito vs analito con color categórico opcional.',
    'analytics.histogram': 'Histograma',
    'analytics.histogramDesc': 'Distribución por grupo superpuesta.',
    'analytics.box': 'Caja',
    'analytics.boxDesc': 'Cuartiles por grupo, valores atípicos mostrados.',
    'analytics.violin': 'Violín',
    'analytics.violinDesc': 'Forma de distribución por grupo, caja interna + media.',
    'analytics.ternary': 'Ternario',
    'analytics.ternaryDesc': 'Composición de tres componentes. Plotly normaliza a 100 automáticamente.',
    'analytics.x': 'X',
    'analytics.y': 'Y',
    'analytics.a': 'A',
    'analytics.b': 'B',
    'analytics.c': 'C',
    'analytics.colourBy': 'Colorear por',
    'analytics.property': 'Propiedad',
    'analytics.groupBy': 'Agrupar por',
    'analytics.logX': 'log X',
    'analytics.logY': 'log Y',
    'analytics.stack': 'Apilar',
    'analytics.overlay': 'en z (superpuesto)',
    'analytics.stacked': 'en y (apilado)',
    'analytics.sideBySide': 'lado a lado',
    'analytics.none': '(ninguno)',
    'analytics.projectsLoaded': '{n} proyecto(s), {m} sondaje(s) cargados',
    'analytics.removeProject': 'Eliminar proyecto {id}',
    'analytics.removeHole': 'Eliminar sondaje {id}',
    'analytics.noProject': 'No hay proyecto cargado. Abra una carpeta desde la barra lateral para cargar datos de ensaye o muestras de superficie, luego vuelva aquí.',
    'analytics.seeDocs': 'Ver documentación de formato →',
    'analytics.chooseData': 'Elija un proyecto o sondaje desde los controles de Datos Analíticos para cargar datos.',
    'analytics.noAssayData': 'El ámbito seleccionado no tiene datos de ensaye representables.',
    'analytics.addCsv': 'Agregue un archivo assays.csv / assays.parquet o surface_samples.csv / surface_samples.parquet a su carpeta de proyecto para habilitar esta página.',
    'analytics.format': 'Formato del proyecto →',
    'scene.title': 'Escena 3D',
    'scene.selectHole': 'Seleccionar un sondaje para agregar',
    'scene.chooseHole': 'Elija un sondaje abajo, luego agréguelo a la escena 3D.',
    'scene.holeLabel': 'Sondaje (cuerda 3D)',
    'scene.pickHole': 'Seleccionar sondaje…',
    'scene.no3dString': 'Sin cuerda 3D',
    'scene.alreadyInScene': 'Ya en escena',
    'scene.addToScene': 'Agregar a escena',
    'scene.holePoint': 'Sondaje (solo punto)',
    'scene.addPoint': 'Agregar punto a escena',
    'scene.holeNoSurvey': 'Sondaje (sin relevamiento)',
    'scene.noSurveyDesc': 'Estos sondajes no tienen traza desveyada ni elevación de collar, por lo que no pueden ubicarse en 3D.',
    'scene.noSurveyCount': '{n} sondaje(s) — sin geometría 3D',
    'scene.holesInScene': 'Sondajes en escena',
    'scene.visibleInScene': 'Visible en la vista 3D. Elimine un sondaje con ×.',
    'scene.remove': 'Eliminar {id}',
    'scene.loadObj': 'Cargar malla OBJ',
    'scene.objInfo': '{name} ({vertexCount} vértices) — {georeferenced}',
    'scene.objGeoref': 'georreferenciado a collares del proyecto',
    'scene.objRaw': 'cargado en coordenadas de malla originales',
    'scene.rawCoords': 'Las coordenadas de la malla no estaban alineadas al proyecto — cargue collares en el mismo SRC para superponer sondajes.',
    'scene.raw': 'sin ref',
    'scene.collars': '{n} collares',
    'scene.inScene': ', {n} en escena',
    'scene.meshes': ', {n} malla(s) OBJ',
    'scene.colorBy': 'Colorear por',
    'scene.none': 'Ninguno',
    'scene.hasAssay': 'Tiene datos de ensaye',
    'scene.geology': 'Geología',
    'scene.structuralDiscs': 'Discos estructurales',
    'scene.stripLogs': 'Perfiles',
    'scene.ortho': 'Ortogonal',
    'scene.persp': 'Perspectiva',
    'scene.legendAssay': 'Leyenda (Tiene datos de ensaye)',
    'scene.hasAssayData': 'Tiene datos de ensaye',
    'scene.noAssayData': 'Sin datos de ensaye',
    'scene.openProject': 'Abra una carpeta de proyecto para ver sondajes.',
    'scene.addHoleSidebar': 'Agregue un sondaje desde la barra lateral para comenzar.',
    'scene.drillholeSelected': 'Sondaje seleccionado',
    'scene.holeId': 'ID de sondaje:',
    'scene.project': 'Proyecto:',
    'scene.na': 'N/D',
    'scene.close': 'Cerrar',
    'map.holeLabel': 'ID de sondaje:',
    'map.projectLabel': 'Proyecto:',
    'map.depthLabel': 'Profundidad:',
    'map.elevationLabel': 'Elevación:',
    'strip.title': 'Perfiles',
    'strip.plots': 'Gráficos',
    'strip.applyBtn': 'Aplicar primer sondaje a todos',
    'strip.applyTooltip': 'Aplicar el sondaje del panel 1 a todos los gráficos',
    'strip.applyDisabled': 'Seleccione un sondaje en el panel 1 y use al menos dos gráficos',
    'strip.startAtZero': 'Comenzar todos los gráficos en 0',
    'strip.alignDepth': 'Alinear todos los gráficos al rango de profundidad',
    'strip.depthRange': 'Rango de profundidad compartido',
    'strip.from': 'Desde (m)',
    'strip.to': 'Hasta (m)',
    'strip.rangeError': 'Ingrese una profundidad final mayor que la inicial.',
    'strip.emptyTitle': 'Ningún proyecto cargado',
    'strip.emptyDesc': 'Abra una carpeta de proyecto para ver los perfiles.',
    'strip.holeCount': '{n} sondajes',
    'strip.loadingHole': 'Cargando datos del sondaje {id}.',
    'strip.projectLabel': 'Proyecto',
    'language.label': 'Idioma',
    'theme.theme': 'Tema',
    'theme.light': 'Tema claro',
    'theme.dark': 'Tema oscuro',
    'map.controls': 'Controles del mapa',
    'map.searchPlaceholder': 'Buscar ID de sondaje',
    'map.noProject': 'Ningún proyecto cargado',
    'map.noProjectDesc': 'Abra una carpeta de proyecto para ver datos de sondajes en el mapa.',
    'map.holeTitle': 'Sondaje {id}',
    'map.openStripLog': 'Abrir perfil',
    'map.noAssayData': 'No se encontraron datos de ensaye para el sondaje {id}.',
    'map.property': 'Propiedad',
    'map.zoom': 'Zoom: {level}',
    'map.collars': '{n} collares',
    'map.assayHoles': '{n} sondajes con ensaye',
    'map.noMatch': 'No se encontró el ID de sondaje',
    'map.pitFootprint': 'Huella del rajo (OSM)',
    'data.title': 'Instrucciones de Formato de Datos',
    'data.lead': 'Drilling Visualisation lee una carpeta de archivos de sondajes. Ábrala desde la barra lateral mediante {openFolder}. El visor detecta los archivos canónicos indicados abajo; cada uno puede entregarse como {parquet} o {csv}. Cuando ambos existen para el mismo nombre, gana la copia Parquet (más pequeña, más rápida de parsear).',
    'data.leadOpen': 'Abrir carpeta del proyecto',
    'data.folderLayout': 'Estructura de carpeta',
    'data.onlyRequired': 'Solo {collars} es obligatorio. Los archivos faltantes se omiten silenciosamente; el visor correspondiente muestra un marcador de posición.',
    'data.canonicalSchema': 'Esquema canónico',
    'data.schemaLead': 'Los nombres de columna siguen los alias de {standardizeColumns} de baselode al pie de la letra — el cargador no hace reasignaciones. Los sinónimos comunes (ej. {lat} para {latitude}, {md} para {depth}) se aceptan porque baselode estandariza al leer.',
    'data.file': 'Archivo',
    'data.requiredColumns': 'Columnas obligatorias',
    'data.optionalColumns': 'Columnas opcionales comunes',
    'data.optAssays': 'una o más columnas de analitos (ej. {au}, {cu})',
    'data.optGeology': '{geoCode}, {geoDesc}, más cualquier atributo por intervalo',
    'data.optStructure': '{alpha}, {beta}, {strike}',
    'data.optSurface': '{elevation}, {sourceId}, {reportNumber}, {projectId}, más columnas de analitos / metadatos (ej. {au}, {cu})',
    'data.optPrecomputed': '— (ya proyectado, XYZ en marco de escena)',
    'data.joinKey': '{holeId} es la clave de unión entre archivos — use la misma cadena en todas partes.',
    'data.geologyFlexible': 'Geología es deliberadamente flexible en esquema: después de las columnas de intervalo obligatorias, cada columna no reservada en {geologyCsv} se traslada al visor. Las columnas numéricas aparecen como trazas de profundidad, las categóricas como franjas de intervalo seleccionables, y las columnas de descripción/comentario como anotaciones de intervalo. Agregar un atributo geológico no requiere una actualización del frontend.',
    'data.projection': 'Proyección',
    'data.projectionDesc': 'La latitud/longitud de los collares se proyecta a un plano tangente local centrado en el centroide de los collares (metros este/norte). Para proyectos de menos de ~100 km esto es visualmente indistinguible de una UTM por zona; para datos a escala continental, proporcione un archivo {precomputed} producido con su propia proyección.',
    'data.projectionDesc2': 'Sistemas de referencia de coordenadas chilenos: La aplicación acepta collares en WGS84 (lat/lng), SIRGAS‑CON (el marco de referencia geodésico sudamericano oficial) y PSAD56 (común en datasets chilenos heredados). Las columnas UTM este/norte se detectan automáticamente y se usan para la georreferenciación de mallas OBJ.',
  },
  pt: {
    'app.title': 'Visualização de Perfurações',
    'nav.map': 'Mapa',
    'nav.stripLogs': 'Perfis',
    'nav.scene3d': 'Cena 3D',
    'nav.analytics': 'Analítica',
    'project.section': 'Projeto',
    'project.dataFormat': 'Instruções de Formato',
    'project.openFolder': 'Abrir pasta do projeto',
    'project.uploadZip': 'Enviar projeto (ZIP)',
    'project.zipInvalid': 'O ZIP não contém arquivos de projeto válidos.',
    'project.loading': 'Carregando…',
    'project.noProject': 'Nenhum projeto carregado',
    'project.close': 'Fechar',
    'project.error': '{msg}',
    'language.label': 'Idioma',
    'theme.theme': 'Tema',
    'demo.section': 'Conjuntos de Demonstração',
    'demo.download': 'Baixar',
    'options.autoLoad': 'Auto-carregar último projeto',
    'analytics.title': 'Dados Analíticos',
    'analytics.loadProject': 'Carregar projeto completo',
    'analytics.selectProject': 'Selecionar projeto…',
    'analytics.filterProject': 'Filtrar furos por projeto',
    'analytics.selectProjectFirst': 'Selecione um projeto primeiro…',
    'analytics.loadHole': 'Carregar furo individual',
    'analytics.selectHole': 'Selecionar furo…',
    'analytics.chooseProject': 'Escolha um projeto acima primeiro…',
    'analytics.addHole': 'Adicionar furo',
    'analytics.clearData': 'Limpar dados de furo',
    'analytics.source': 'Fonte de dados',
    'analytics.rows': '{n} linhas',
    'analytics.drillholeAssays': 'Ensaios de furos',
    'analytics.surfaceSamples': 'Amostras de superfície',
    'analytics.scatter': 'Dispersão',
    'analytics.scatterDesc': 'Analito vs analito com cor categórica opcional.',
    'analytics.histogram': 'Histograma',
    'analytics.histogramDesc': 'Distribuição por grupo sobreposta.',
    'analytics.box': 'Caixa',
    'analytics.boxDesc': 'Quartis por grupo, outliers mostrados.',
    'analytics.violin': 'Violino',
    'analytics.violinDesc': 'Forma de distribuição por grupo, caixa interna + média.',
    'analytics.ternary': 'Ternário',
    'analytics.ternaryDesc': 'Composição de três componentes. Plotly normaliza para 100 automaticamente.',
    'analytics.x': 'X',
    'analytics.y': 'Y',
    'analytics.a': 'A',
    'analytics.b': 'B',
    'analytics.c': 'C',
    'analytics.colourBy': 'Colorir por',
    'analytics.property': 'Propriedade',
    'analytics.groupBy': 'Agrupar por',
    'analytics.logX': 'log X',
    'analytics.logY': 'log Y',
    'analytics.stack': 'Empilhar',
    'analytics.overlay': 'em z (sobreposto)',
    'analytics.stacked': 'em y (empilhado)',
    'analytics.sideBySide': 'lado a lado',
    'analytics.none': '(nenhum)',
    'analytics.projectsLoaded': '{n} projeto(s), {m} furo(s) carregados',
    'analytics.removeProject': 'Remover projeto {id}',
    'analytics.removeHole': 'Remover furo {id}',
    'analytics.noProject': 'Nenhum projeto carregado. Abra uma pasta pela barra lateral para carregar dados de ensaio ou amostras de superfície, depois volte aqui.',
    'analytics.seeDocs': 'Ver documentação de formato →',
    'analytics.chooseData': 'Escolha um projeto ou furo pelos controles de Dados Analíticos para carregar dados.',
    'analytics.noAssayData': 'O escopo selecionado não possui dados de ensaio representáveis.',
    'analytics.addCsv': 'Adicione um arquivo assays.csv / assays.parquet ou surface_samples.csv / surface_samples.parquet à sua pasta de projeto para habilitar esta página.',
    'analytics.format': 'Formato do projeto →',
    'scene.title': 'Cena 3D',
    'scene.selectHole': 'Selecionar um furo para adicionar',
    'scene.chooseHole': 'Escolha um furo abaixo e adicione à cena 3D.',
    'scene.holeLabel': 'Furo (corda 3D)',
    'scene.pickHole': 'Selecionar furo…',
    'scene.no3dString': 'Sem corda 3D',
    'scene.alreadyInScene': 'Já na cena',
    'scene.addToScene': 'Adicionar à cena',
    'scene.holePoint': 'Furo (apenas ponto)',
    'scene.addPoint': 'Adicionar ponto à cena',
    'scene.holeNoSurvey': 'Furo (sem levantamento)',
    'scene.noSurveyDesc': 'Estes furos não têm traço dessurveyado nem elevação de colar, então não podem ser posicionados em 3D.',
    'scene.noSurveyCount': '{n} furo(s) — sem geometria 3D',
    'scene.holesInScene': 'Furos na cena',
    'scene.visibleInScene': 'Visível na vista 3D. Remova um furo com ×.',
    'scene.remove': 'Remover {id}',
    'scene.loadObj': 'Carregar malha OBJ',
    'scene.objInfo': '{name} ({vertexCount} vértices) — {georeferenced}',
    'scene.objGeoref': 'georreferenciado a colares do projeto',
    'scene.objRaw': 'carregado em coordenadas de malha originais',
    'scene.rawCoords': 'As coordenadas da malha não estavam alinhadas ao projeto — carregue colares no mesmo SRC para sobrepor furos.',
    'scene.raw': 'sem ref',
    'scene.collars': '{n} colares',
    'scene.inScene': ', {n} em cena',
    'scene.meshes': ', {n} malha(s) OBJ',
    'scene.colorBy': 'Colorir por',
    'scene.none': 'Nenhum',
    'scene.hasAssay': 'Tem dados de ensaio',
    'scene.geology': 'Geologia',
    'scene.structuralDiscs': 'Discos estruturais',
    'scene.stripLogs': 'Perfis',
    'scene.ortho': 'Ortogonal',
    'scene.persp': 'Perspectiva',
    'scene.legendAssay': 'Legenda (Tem dados de ensaio)',
    'scene.hasAssayData': 'Tem dados de ensaio',
    'scene.noAssayData': 'Sem dados de ensaio',
    'scene.openProject': 'Abra uma pasta de projeto para ver furos.',
    'scene.addHoleSidebar': 'Adicione um furo pela barra lateral para começar.',
    'scene.drillholeSelected': 'Furo selecionado',
    'scene.holeId': 'ID do furo:',
    'scene.project': 'Projeto:',
    'scene.na': 'N/D',
    'scene.close': 'Fechar',
    'map.holeLabel': 'ID do furo:',
    'map.projectLabel': 'Projeto:',
    'map.depthLabel': 'Profundidade:',
    'map.elevationLabel': 'Elevação:',
    'strip.title': 'Perfis',
    'strip.plots': 'Gráficos',
    'strip.applyBtn': 'Aplicar primeiro furo a todos',
    'strip.applyTooltip': 'Aplicar o furo do painel 1 a todos os gráficos',
    'strip.applyDisabled': 'Selecione um furo no painel 1 e use pelo menos dois gráficos',
    'strip.startAtZero': 'Iniciar todos os gráficos em 0',
    'strip.alignDepth': 'Alinhar todos os gráficos ao intervalo de profundidade',
    'strip.depthRange': 'Intervalo de profundidade compartilhado',
    'strip.from': 'De (m)',
    'strip.to': 'Até (m)',
    'strip.rangeError': 'Insira uma profundidade final maior que a inicial.',
    'strip.emptyTitle': 'Nenhum projeto carregado',
    'strip.emptyDesc': 'Abra uma pasta de projeto para ver os perfis.',
    'strip.holeCount': '{n} furos',
    'strip.loadingHole': 'Carregando dados do furo {id}.',
    'strip.projectLabel': 'Projeto',
    'theme.light': 'Tema claro',
    'theme.dark': 'Tema escuro',
    'map.controls': 'Controles do mapa',
    'map.searchPlaceholder': 'Buscar ID de sondagem',
    'map.noProject': 'Nenhum projeto carregado',
    'map.noProjectDesc': 'Abra uma pasta de projeto para ver dados de sondagens no mapa.',
    'map.holeTitle': 'Sondagem {id}',
    'map.openStripLog': 'Abrir perfil',
    'map.noAssayData': 'Nenhum dado de ensaio encontrado para a sondagem {id}.',
    'map.property': 'Propriedade',
    'map.zoom': 'Zoom: {level}',
    'map.collars': '{n} colares',
    'map.assayHoles': '{n} sondagens com ensaio',
    'map.noMatch': 'ID de sondagem não encontrado',
    'map.pitFootprint': 'Pegada da cava (OSM)',
    'data.title': 'Instruções de Formato de Dados',
    'data.lead': 'Drilling Visualisation lê uma pasta de arquivos de sondagens. Abra-a pela barra lateral através de {openFolder}. O visor detecta os arquivos canônicos abaixo; cada um pode ser fornecido como {parquet} ou {csv}. Quando ambos existem para o mesmo nome, a cópia Parquet vence (menor, mais rápida de analisar).',
    'data.leadOpen': 'Abrir pasta do projeto',
    'data.folderLayout': 'Estrutura de pasta',
    'data.onlyRequired': 'Apenas {collars} é obrigatório. Arquivos faltantes são ignorados silenciosamente; o visor correspondente mostra um marcador de posição.',
    'data.canonicalSchema': 'Esquema canônico',
    'data.schemaLead': 'Os nomes das colunas seguem os aliases de {standardizeColumns} do baselode à risca — o carregador não faz remapeamento. Sinônimos comuns (ex. {lat} para {latitude}, {md} para {depth}) são aceitos porque o baselode padroniza ao ler.',
    'data.file': 'Arquivo',
    'data.requiredColumns': 'Colunas obrigatórias',
    'data.optionalColumns': 'Colunas opcionais comuns',
    'data.optAssays': 'uma ou mais colunas de analitos (ex. {au}, {cu})',
    'data.optGeology': '{geoCode}, {geoDesc}, mais quaisquer atributos por intervalo',
    'data.optStructure': '{alpha}, {beta}, {strike}',
    'data.optSurface': '{elevation}, {sourceId}, {reportNumber}, {projectId}, mais colunas de analitos / metadados (ex. {au}, {cu})',
    'data.optPrecomputed': '— (já projetado, XYZ no quadro da cena)',
    'data.joinKey': '{holeId} é a chave de junção entre arquivos — use a mesma string em todos os lugares.',
    'data.geologyFlexible': 'Geologia é deliberadamente flexível em esquema: após as colunas de intervalo obrigatórias, toda coluna não reservada em {geologyCsv} é levada ao visor. Colunas numéricas aparecem como traços de profundidade, categóricas como faixas de intervalo selecionáveis, e colunas de descrição/comentário como anotações de intervalo. Adicionar um atributo geológico não requer atualização do frontend.',
    'data.projection': 'Projeção',
    'data.projectionDesc': 'A latitude/longitude dos colares é projetada para um plano tangente local centrado no centroide dos colares (metros leste/norte). Para projetos de menos de ~100 km, isso é visualmente indistinguível de uma UTM por zona; para dados em escala continental, forneça um arquivo {precomputed} produzido com sua própria projeção.',
    'data.projectionDesc2': 'Sistemas de referência de coordenadas chilenos: A aplicação aceita colares em WGS84 (lat/lng), SIRGAS‑CON (o referencial geodésico sul-americano oficial) e PSAD56 (comum em conjuntos de dados chilenos legados). As colunas UTM leste/norte são detectadas automaticamente e usadas para o georreferenciamento de malhas OBJ.',
  },
  en: {
    'app.title': 'Drilling Visualisation',
    'nav.map': 'Map',
    'nav.stripLogs': 'Strip Logs',
    'nav.scene3d': '3D Scene',
    'nav.analytics': 'Analytics',
    'project.section': 'Project',
    'project.dataFormat': 'Data Format Instructions',
    'project.openFolder': 'Open project folder',
    'project.uploadZip': 'Upload project (ZIP)',
    'project.zipInvalid': 'The ZIP does not contain valid project files.',
    'project.loading': 'Loading…',
    'project.noProject': 'No project loaded',
    'project.close': 'Close',
    'project.error': '{msg}',
    'language.label': 'Language',
    'theme.theme': 'Theme',
    'demo.section': 'Demo Datasets',
    'demo.download': 'Download',
    'options.autoLoad': 'Auto-load last project',
    'analytics.title': 'Analytics Data',
    'analytics.loadProject': 'Load entire project',
    'analytics.selectProject': 'Select project…',
    'analytics.filterProject': 'Filter single holes by project',
    'analytics.selectProjectFirst': 'Select project first…',
    'analytics.loadHole': 'Load single hole',
    'analytics.selectHole': 'Select hole…',
    'analytics.chooseProject': 'Choose a project above first…',
    'analytics.addHole': 'Add hole',
    'analytics.clearData': 'Clear drillhole data',
    'analytics.source': 'Data source',
    'analytics.rows': '{n} rows',
    'analytics.drillholeAssays': 'Drillhole assays',
    'analytics.surfaceSamples': 'Surface samples',
    'analytics.scatter': 'Scatter',
    'analytics.scatterDesc': 'Analyte vs analyte with optional categorical colour-by.',
    'analytics.histogram': 'Histogram',
    'analytics.histogramDesc': 'Distribution per group overlaid.',
    'analytics.box': 'Box',
    'analytics.boxDesc': 'Quartiles per group, outliers shown.',
    'analytics.violin': 'Violin',
    'analytics.violinDesc': 'Distribution shape per group, inner box + mean line.',
    'analytics.ternary': 'Ternary',
    'analytics.ternaryDesc': 'Three-component composition. Plotly auto-normalises components to 100.',
    'analytics.x': 'X',
    'analytics.y': 'Y',
    'analytics.a': 'A',
    'analytics.b': 'B',
    'analytics.c': 'C',
    'analytics.colourBy': 'Colour by',
    'analytics.property': 'Property',
    'analytics.groupBy': 'Group by',
    'analytics.logX': 'log X',
    'analytics.logY': 'log Y',
    'analytics.stack': 'Stack',
    'analytics.overlay': 'in z (overlay)',
    'analytics.stacked': 'in y (stacked)',
    'analytics.sideBySide': 'side-by-side',
    'analytics.none': '(none)',
    'analytics.projectsLoaded': '{n} project(s), {m} hole(s) loaded',
    'analytics.removeProject': 'Remove project {id}',
    'analytics.removeHole': 'Remove hole {id}',
    'analytics.noProject': 'No project loaded. Open a project folder from the sidebar to load assay or surface sample data, then return here.',
    'analytics.seeDocs': 'See the project format docs →',
    'analytics.chooseData': 'Choose a project or hole from the Analytics data controls to load drillhole data.',
    'analytics.noAssayData': 'The selected drillhole scope has no plottable assay data.',
    'analytics.addCsv': 'Add an assays.csv / assays.parquet or a surface_samples.csv / surface_samples.parquet to your project folder to enable this page.',
    'analytics.format': 'Project format →',
    'scene.title': '3D Scene',
    'scene.selectHole': 'Select a hole to add',
    'scene.chooseHole': 'Choose a hole below, then add it to the 3D scene.',
    'scene.holeLabel': 'Hole (3D string)',
    'scene.pickHole': 'Pick a hole…',
    'scene.no3dString': 'No holes with a 3D string',
    'scene.alreadyInScene': 'Already in scene',
    'scene.addToScene': 'Add to scene',
    'scene.holePoint': 'Hole (point only)',
    'scene.addPoint': 'Add point to scene',
    'scene.holeNoSurvey': 'Hole (no survey)',
    'scene.noSurveyDesc': 'These holes have no desurveyed trace and no collar elevation, so they can\'t be placed in 3D.',
    'scene.noSurveyCount': '{n} hole(s) — no 3D geometry',
    'scene.holesInScene': 'Holes in scene',
    'scene.visibleInScene': 'Visible in the 3D view. Remove a hole with ×.',
    'scene.remove': 'Remove {id}',
    'scene.loadObj': 'Load OBJ mesh',
    'scene.objInfo': '{name} ({vertexCount} vertices) — {georeferenced}',
    'scene.objGeoref': 'georeferenced to project collars',
    'scene.objRaw': 'loaded in raw mesh coordinates',
    'scene.rawCoords': 'Mesh coordinates were not aligned to the project — load collars in the same CRS to overlay drillholes.',
    'scene.raw': 'raw',
    'scene.collars': '{n} collars',
    'scene.inScene': ', {n} in scene',
    'scene.meshes': ', {n} OBJ mesh(es)',
    'scene.colorBy': 'Color by',
    'scene.none': 'None',
    'scene.hasAssay': 'Has Assay Data',
    'scene.geology': 'Geology',
    'scene.structuralDiscs': 'Structural discs',
    'scene.stripLogs': 'Strip logs',
    'scene.ortho': 'Ortho',
    'scene.persp': 'Persp',
    'scene.legendAssay': 'Legend (Has Assay Data)',
    'scene.hasAssayData': 'Has assay data',
    'scene.noAssayData': 'No assay data',
    'scene.openProject': 'Open a project folder to view drillholes.',
    'scene.addHoleSidebar': 'Add a hole from the sidebar to begin.',
    'scene.drillholeSelected': 'Drillhole selected',
    'scene.holeId': 'Hole ID:',
    'scene.project': 'Project:',
    'scene.na': 'N/A',
    'scene.close': 'Close',
    'map.holeLabel': 'Hole ID:',
    'map.projectLabel': 'Project:',
    'map.depthLabel': 'Depth:',
    'map.elevationLabel': 'Elevation:',
    'strip.title': 'Strip Logs',
    'strip.plots': 'Plots',
    'strip.applyBtn': 'Apply first hole to all graphs',
    'strip.applyTooltip': 'Apply the selected hole from panel 1 to every graph',
    'strip.applyDisabled': 'Select a hole in panel 1 and use at least two plots',
    'strip.startAtZero': 'Start all graphs at 0',
    'strip.alignDepth': 'Align all graphs to depth range',
    'strip.depthRange': 'Shared strip log depth range',
    'strip.from': 'From (m)',
    'strip.to': 'To (m)',
    'strip.rangeError': 'Enter an end depth greater than the start depth.',
    'strip.emptyTitle': 'No project loaded',
    'strip.emptyDesc': 'Open a project folder to view strip logs.',
    'strip.holeCount': '{n} holes',
    'strip.loadingHole': 'Loading data for hole {id}.',
    'strip.projectLabel': 'Project',
    'theme.light': 'Light theme',
    'theme.dark': 'Dark theme',
    'map.controls': 'Map controls',
    'map.searchPlaceholder': 'Search Hole ID',
    'map.noProject': 'No project loaded',
    'map.noProjectDesc': 'Open a project folder to view drillhole data on the map.',
    'map.holeTitle': 'Hole {id}',
    'map.openStripLog': 'Open strip log',
    'map.noAssayData': 'No assay data found for hole {id}.',
    'map.property': 'Property',
    'map.zoom': 'Zoom: {level}',
    'map.collars': '{n} collars',
    'map.assayHoles': '{n} assay holes',
    'map.noMatch': 'No matching Hole ID',
    'map.pitFootprint': 'Pit footprint (OSM)',
    'data.title': 'Data Format Instructions',
    'data.lead': 'Drilling Visualisation reads a folder of drillhole files. Open it from the sidebar via {openFolder}. The viewer detects the canonical files below; each one can be supplied as {parquet} or {csv}. When both exist for the same name, the Parquet copy wins (smaller, faster to parse).',
    'data.leadOpen': 'Open project folder',
    'data.folderLayout': 'Folder layout',
    'data.onlyRequired': 'Only {collars} is required. Missing files are skipped silently; the relevant viewer falls back to a placeholder.',
    'data.canonicalSchema': 'Canonical schema',
    'data.schemaLead': 'Column names match {standardizeColumns} aliases verbatim — the loader does no remapping. Common synonyms (e.g. {lat} for {latitude}, {md} for {depth}) are accepted because baselode standardises on read.',
    'data.file': 'File',
    'data.requiredColumns': 'Required columns',
    'data.optionalColumns': 'Common optional columns',
    'data.optAssays': 'one or more analyte columns (e.g. {au}, {cu})',
    'data.optGeology': '{geoCode}, {geoDesc}, plus any intervalled attributes',
    'data.optStructure': '{alpha}, {beta}, {strike}',
    'data.optSurface': '{elevation}, {sourceId}, {reportNumber}, {projectId}, plus any analyte / metadata columns (e.g. {au}, {cu})',
    'data.optPrecomputed': '— (already-projected, scene-frame XYZ)',
    'data.joinKey': '{holeId} is the join key across files — use the same string everywhere.',
    'data.geologyFlexible': 'Geology is deliberately schema-flexible: after the required interval columns, every non-reserved column in {geologyCsv} is carried into the Viewer. Numeric columns appear as depth tracks, categorical columns as selectable interval strips, and description/comment columns as interval annotations. Adding a geology attribute does not require a frontend update.',
    'data.projection': 'Projection',
    'data.projectionDesc': 'Collar latitude/longitude are projected to a local tangent plane centred on the collar centroid (metres east/north). For projects spanning under ~100 km this is visually indistinguishable from a per-zone UTM; for continental-scale data, supply a {precomputed} file produced with your own projection.',
    'data.projectionDesc2': 'Chilean coordinate reference systems: The application accepts collars in WGS84 (lat/lng), SIRGAS‑CON (the official South American geodetic reference), and PSAD56 (common in legacy Chilean datasets). UTM easting/northing columns are automatically detected and used for OBJ mesh georeferencing.',
  },
};

function readInitialLanguage() {
  if (typeof window === 'undefined') return FALLBACK;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && VALID.has(stored)) return stored;
  } catch (e) { /* ignore */ }
  return FALLBACK;
}

const LanguageContext = createContext({
  lang: FALLBACK,
  setLang: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLanguage);

  useEffect(() => {
    document.documentElement.lang = lang;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* ignore */ }
  }, [lang]);

  const setLang = useCallback((next) => {
    if (VALID.has(next)) setLangState(next);
  }, []);

  const translate = useCallback((key, params = {}) => {
    const text = t[lang]?.[key] ?? t[FALLBACK]?.[key] ?? key;
    if (!params || Object.keys(params).length === 0) return text;
    const hasElements = Object.values(params).some(
      (v) => v !== null && v !== undefined && typeof v === 'object'
    );
    if (hasElements) {
      const parts = text.split(/(\{[^}]+\})/g);
      return parts.map((part) => {
        const m = part.match(/^\{(\w+)\}$/);
        return m && params[m[1]] !== undefined ? params[m[1]] : part;
      });
    }
    return text.replace(/\{(\w+)\}/g, (_, k) => params[k] !== undefined ? String(params[k]) : `{${k}}`);
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translate }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
